import { Component, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable, observable } from "mobx";
import { type SiteCreate, type Site } from "../../api";
import { doubleBindString } from "../../utils/double-bind";
import { type FormCreateProps } from "../../utils/form-create-props";
import { createUuid } from "../../utils/uuid";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";

export interface FormCreateSiteProps extends FormCreateProps<Site> {}

import { store } from "../../store";

@observer
export class FormCreateSite extends Component<FormCreateSiteProps> {
    @observable public accessor label = "";

    private validationId = createUuid();

    constructor(props: FormCreateSiteProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositorySites.validation.create.updateModel(this.validationId, this.siteCreate);
    }

    componentDidMount(): void {
        store.repositorySites.validation.create.initializeModel(this.validationId, this.siteCreate);
    }

    @computed private get siteCreate(): SiteCreate {
        return pick(["label"], this as FormCreateSite);
    }

    @action.bound
    private async createSite(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const site = await store.repositorySites.create(this.siteCreate);
        this.reset();
        if (this.props.onCreate) {
            this.props.onCreate(site);
        }
    }

    @action.bound private reset(): void {
        this.label = "";
        this.triggerValidation();
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formCreateSite.submit.text")}
                validation={store.repositorySites.validation.create}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.createSite}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formCreateSite.label.label")}
                        {...doubleBindString<FormCreateSite>(this, "label", () =>
                            this.triggerValidation(),
                        )}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositorySites.validation.create.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formCreateSite.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
